import sprite from '../icons/sprite.svg?raw';
import styles from './icon.module.css';
import { type IconName } from '../icons/names';

export { ICON_NAMES, type IconName } from '../icons/names';

/**
 * One set of little pictures. Brand guidelines section 06.
 *
 * Thick, chunky, all drawn the same way, and all from `slab-regular/`. They are
 * the friendliest thing the brand owns, which is exactly why they are never
 * allowed to do a number's job: five stars is a drawing, `14h 20m from 1,904
 * players` is a measurement. Where a number is the point, set the number and
 * let the icon sit beside it.
 *
 * ## Why a sheet rather than a hundred components
 *
 * The whole set is one `<symbol>` sheet welded by `tools/icon-sprite.mjs` and
 * dropped into the document once by `IconSheet`. Every `Icon` after that is a
 * nine-byte `<use>`, so a page with forty icons on it carries forty references
 * rather than forty copies of a path.
 *
 * It is inlined into the bundle rather than fetched from `public/`, because a
 * cross-document `<use href="/icons.svg#i-play">` is the one form of this that
 * Safari has never reliably resolved — and an icon set that silently renders
 * nothing on one browser is worse than no icon set.
 */

/**
 * The sheet, rendered once at the root of the application.
 *
 * `innerHTML` because the sheet is a build artefact rather than a component
 * tree, and because React cannot render a `<symbol>` list any more cheaply than
 * the parser can. The string comes from a file in this repository at build
 * time; there is no path by which anything a person typed reaches it.
 */
export function IconSheet(): React.ReactElement {
  return <span className={styles.sheet} dangerouslySetInnerHTML={{ __html: sprite }} />;
}

/**
 * Four sizes and no others: 40, 24, 16, or sitting inside a line of text.
 *
 * `inline` is the fourth, and it is an em rather than a pixel count so that an
 * icon in a sentence rides the type it is set in and moves with it.
 */
export type IconSize = 'inline' | 16 | 24 | 40;

const SIZE_CLASS: Record<string, string> = {
  inline: styles.inline ?? '',
  16: styles.sm ?? '',
  24: styles.md ?? '',
  40: styles.lg ?? '',
};

/**
 * An icon.
 *
 * `label` is what makes it an image rather than furniture. Without one it is
 * `aria-hidden`, which is right for the overwhelming majority: section 06 says
 * to put a word next to an icon whenever it is not obvious, so the word is
 * usually already there and a label would make a screen reader read it twice.
 * Pass one only where the icon is genuinely alone and carries meaning.
 */
export function Icon({
  name,
  size = 'inline',
  label,
  className,
}: {
  name: IconName;
  size?: IconSize;
  /** Say it out loud. Omit where a visible word already says the same thing. */
  label?: string;
  /**
   * `| undefined` explicitly, because `exactOptionalPropertyTypes` is on: a
   * caller reading a class off a CSS module gets `string | undefined`, and
   * without this every such call site is a type error.
   */
  className?: string | undefined;
}): React.ReactElement {
  const classes = [styles.icon, SIZE_CLASS[String(size)], className].filter(Boolean).join(' ');

  return (
    <svg
      className={classes}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      focusable="false"
    >
      <use href={`#i-${name}`} />
    </svg>
  );
}

/**
 * The rounded box an icon can sit in. Section 06's chip.
 *
 * Same thin edge as a pill. `tone` is what the box is filled with, and the
 * short list is the whole of it: the panel's own accent, Ink, one of the two
 * fixed signals, or nothing at all.
 *
 * **`live` and `countdown` are named for the meaning, not the colour.** Volt is
 * only ever "happening right now" and Solar is only ever "there is a clock on
 * this", on every surface — so neither is the panel's to choose, and a chip
 * asking for lime because lime looked good is the mistake section 04 spends a
 * page on.
 */
export function IconChip({
  name,
  tone = 'plain',
  size = 24,
  label,
}: {
  name: IconName;
  tone?: 'plain' | 'accent' | 'ink' | 'live' | 'countdown';
  size?: 16 | 24 | 40;
  label?: string;
}): React.ReactElement {
  const TONE_CLASS: Record<string, string> = {
    plain: styles.chipPlain ?? '',
    accent: styles.chipAccent ?? '',
    ink: styles.chipInk ?? '',
    live: styles.chipLive ?? '',
    countdown: styles.chipCountdown ?? '',
  };

  const BOX_CLASS: Record<number, string> = {
    16: styles.chipSm ?? '',
    24: styles.chipMd ?? '',
    40: styles.chipLg ?? '',
  };

  return (
    <span className={[styles.chip, TONE_CLASS[tone], BOX_CLASS[size]].filter(Boolean).join(' ')}>
      <Icon name={name} {...(label ? { label } : {})} />
    </span>
  );
}

/**
 * The twelve with a fixed job. Section 06.
 *
 * *"Once an icon means one of these things, it never means anything else,
 * anywhere."* Reaching for `ICON_JOB.hours` rather than typing `'clock'` is
 * what keeps that true: the clock is hours played, and a screen that wanted a
 * clock for something else has to say so out loud by naming the icon directly,
 * where a reviewer can see it.
 *
 * `notANumber` is the twelfth and it is a prohibition rather than a picture: it
 * is what the guidelines put beside a star to say a star is not a measurement.
 */
export const ICON_JOB = {
  /** Happening right now. Always Volt, never anything else. */
  live: 'signal-bars',
  /** Anything with a clock on it: a playtest window, a launch day. */
  countdown: 'stopwatch',
  /** Update history. The strip's own icon. */
  updates: 'chart-bar',
  /** A creator. */
  creator: 'video',
  /** Hours played. */
  hours: 'clock',
  /** Reached the end. */
  finished: 'flag',
  /** A playtest. */
  playtest: 'ticket',
  /** A free key. */
  key: 'key',
  /** A creator's share of the money. */
  share: 'percent',
  /** Getting a build onto a machine. */
  download: 'arrow-down-to-line',
  /** One of our games. */
  game: 'gamepad',
  /** A star is not a number. */
  notANumber: 'star',
} as const satisfies Record<string, IconName>;
